import React from "react";
import { Admin, DashboardComponent } from "react-admin";
import { useDispatch } from "react-redux";
import { History } from "history";
import { I18nProvider } from "react-admin";
import { ApiManager } from "../data/ApiManager";
import { ThemeOptions } from "@material-ui/core";
import { ProLogsPermissions } from "../auth/permissions";
import { getAdminResources } from "./adminResources";
import { useEffect } from "react";
import { AuthManager } from "../auth/Auth";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { createSetFleetManagerHubAction } from "../redux/actions/fleetManagerHub";
import { Dashboard } from "../../components/dashboard/Dashboard";
import { MaintenanceDashboardSelectedVehiclesContext } from "../../components/assets/maintenance-dashboard/MaintenanceDashboardSelectedVehiclesContext";
import { SafetyDashboardSelectedDriversContext } from "../../components/safety/safety-dashboard/SafetyDashboardSelectedDriversContext";
import { ProLogsThemeProvider } from "../../components/layout/ThemeProvider";
import { AppBarFiltersContainerContextProvider } from "../../components/common/app-bar-filters/AppBarFiltersContainerContextProvider";
import { useLogoutWhenInactive } from "../hooks/useLogoutWhenInactive";
import { useUserActivityTracker } from "../../hooks/useUserActivityTracker";
import { useAxiosInstance } from "../hooks/useAxiosInstance";
import { activityService } from "../../services/activityService";
import { SignalRProvider, useSignalR } from "../../context/SignalRContext";
import { ImportDataProvider } from "../../context/ImportDataContext";
import SignalRPopup from "../../components/common/signal-r-popup/SignalRPopup";
import { ServerMaintenanceDialog } from "../../components/layout/ServerMaintenanceDialog";
import { SignalROidcRecovery } from "../auth/signalROidcRecovery";
import { useDisconnectedDrivingNotifications } from "../hooks/useDisconnectedDrivingNotifications";

interface IAdminWrapperProps {
  history: History<{}>;
  dataProvider: ApiManager;
  authProvider: AuthManager;
  i18nProvider: I18nProvider;
  loginPage: (props: any) => JSX.Element;
  layout: (props: any) => JSX.Element;
  dashboard?: DashboardComponent;
  customRoutes: JSX.Element[];
  theme?: ThemeOptions;
}

const AdminWrapperContent = (props: IAdminWrapperProps) => {
  const { dataProvider, authProvider, history } = props;
  const dispatch = useDispatch();
  const axios = useAxiosInstance();
  const { messages, removeMessage } = useSignalR();
  useDisconnectedDrivingNotifications(authProvider);

  React.useLayoutEffect(() => {
    authProvider.init({
      clientId: process.env["REACT_APP_AUTH_CLIENT_ID"] ?? "",
      authority: process.env["REACT_APP_AUTH_AUTHORITY"] ?? "",
      redirectUri: process.env["REACT_APP_AUTH_REDIRECT_URI"] ?? "",
      silentRedirectUri:
        process.env["REACT_APP_AUTH_SILENT_REDIRECT_URI"] ?? "",
      postLogoutRedirectUri:
        process.env["REACT_APP_AUTH_POST_LOGOUT_REDIRECT_URI"] ?? "",
    });

    dataProvider.init(dispatch);
  }, [dataProvider, authProvider, dispatch]);

  useLogoutWhenInactive(authProvider, history);

  useUserActivityTracker({
    pingIntervalMinutes: 5,
    inactivityTimeoutMinutes: 5,
    enableLogging: process.env.NODE_ENV === "development",
    onActivityPing: async () => {
      if (axios) {
        await activityService.pingActivity(axios);
      }
    },
  });

  useEffect(() => {
    let hubConnection: HubConnection | undefined;
    authProvider.checkAuth(null).then(async () => {
      const apiUrl =
        process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
        "https://localhost:44327";
      const signalROidcRecovery = new SignalROidcRecovery(
        authProvider,
        (retryContext) => {
          if (retryContext.elapsedMilliseconds < 5 * 60 * 1000) {
            console.log("Attempt to reconnect to the hub...");
            return Math.random() * 10000;
          }

          console.warn(
            "Failed to reconnect to the hub for more than 5 minutes, aborting."
          );
          return null;
        }
      );
      hubConnection = new HubConnectionBuilder()
        .withUrl(`${apiUrl}/signalr-hubs/fleet-manager-hub`, {
          accessTokenFactory: signalROidcRecovery.accessTokenFactory,
        })
        .withAutomaticReconnect(signalROidcRecovery.retryPolicy)
        .configureLogging(LogLevel.Information)
        .build();

      hubConnection.on("OnPasswordChanged", () =>
        history.push("/logout?reason=password-changed")
      );
      hubConnection.onreconnected(signalROidcRecovery.markConnected);

      try {
        await signalROidcRecovery.start(() => hubConnection!.start());
        dispatch(createSetFleetManagerHubAction({ hub: hubConnection }));
      } catch (error) {
        console.error("Failed to start fleet-manager-hub connection", error);
        dispatch(createSetFleetManagerHubAction({}));
      }
    });

    return () => {
      if (hubConnection) {
        dispatch(createSetFleetManagerHubAction({}));
        hubConnection.off("OnPasswordChanged");
        hubConnection.stop();
      }
    };
  }, [authProvider, dispatch, history]);

  return (
    <>
      <AppBarFiltersContainerContextProvider>
        <MaintenanceDashboardSelectedVehiclesContext>
          <SafetyDashboardSelectedDriversContext>
            <ProLogsThemeProvider>
              <Admin dashboard={Dashboard} {...props} disableTelemetry>
                {(perms: ProLogsPermissions) => {
                  const { actionPermissions: permissions } = perms;
                  return getAdminResources(permissions);
                }}
              </Admin>
              <ServerMaintenanceDialog
                authProvider={authProvider}
                dataProvider={dataProvider}
              />
            </ProLogsThemeProvider>
          </SafetyDashboardSelectedDriversContext>
        </MaintenanceDashboardSelectedVehiclesContext>
      </AppBarFiltersContainerContextProvider>
      <SignalRPopup messages={messages} onClose={removeMessage} />
    </>
  );
};

export const AdminWrapper = (props: IAdminWrapperProps) => {
  return (
    <SignalRProvider>
      <ImportDataProvider>
        <AdminWrapperContent {...props} />
      </ImportDataProvider>
    </SignalRProvider>
  );
};
